import React, { useState } from "react";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Progress } from "@/components/ui/progress";
import { FileText, ChevronDown, ChevronUp, Sparkles, Loader2 } from "lucide-react";
import AssessmentDrawer from "./AssessmentDrawer";
import { RequirementDocument } from "@/types/document";

type InlineDocumentInfo = Pick<RequirementDocument, 'id' | 'file_name' | 'relevance_score' | 'assessment' | 'citations'>;

interface InlineDocumentListProps {
  documents: InlineDocumentInfo[];
  isExpanded: boolean;
  onToggle: () => void;
  isLoading?: boolean;
}

export function InlineDocumentList({ 
  documents, 
  isExpanded, 
  onToggle,
  isLoading = false
}: InlineDocumentListProps) {
  const [assessmentDoc, setAssessmentDoc] = useState<InlineDocumentInfo | null>(null);
  const [isAssessmentOpen, setIsAssessmentOpen] = useState(false);

  if (documents.length === 0 && !isLoading) {
    return null;
  }

  const handleViewAssessment = (doc: InlineDocumentInfo) => {
    setAssessmentDoc(doc);
    setIsAssessmentOpen(true);
  };

  return (
    <div className="mt-2">
      <Button 
        variant="ghost" 
        size="sm" 
        className="h-6 px-2 text-xs flex items-center gap-1 mb-1 text-muted-foreground"
        onClick={onToggle}
      >
        <FileText className="h-3 w-3" />
        <span>{isLoading ? "Loading..." : `${documents.length} Document${documents.length !== 1 ? 's' : ''}`}</span>
        {isExpanded ? <ChevronUp className="h-3 w-3" /> : <ChevronDown className="h-3 w-3" />}
      </Button>
      
      {isExpanded && (
        <div className="space-y-2 pl-2 border-l-2 border-muted ml-1">
          {isLoading ? (
            <div className="bg-muted/30 rounded p-2 text-xs">
              Loading documents...
            </div>
          ) : documents.length > 0 ? (
            documents.map(doc => (
              <div key={doc.id} className="bg-muted/30 rounded p-2 text-xs">
                <div className="flex items-center justify-between">
                  <span className="font-medium">{doc.file_name}</span>
                  {doc.assessment && (
                    <Button 
                      variant="ghost" 
                      size="sm" 
                      className="h-5 px-2 text-xs text-primary flex items-center gap-1"
                      title="View AI assessment"
                      onClick={() => handleViewAssessment(doc)}
                    >
                      <Sparkles className="h-3 w-3" />
                      <span>AI Assessment</span>
                    </Button>
                  )}
                </div>
                
                {doc.relevance_score !== null ? (
                  <div className="mt-1 space-y-1">
                    <div className="flex justify-between items-center">
                      <span className="text-muted-foreground">Relevance</span>
                      <Badge variant="outline" className="bg-primary/5 h-5">
                        {doc.relevance_score}%
                      </Badge>
                    </div>
                    <Progress value={doc.relevance_score} className="h-1" />
                  </div>
                ) : (
                  <div className="mt-1 flex items-center text-primary text-xs gap-1">
                    <Sparkles className="h-3 w-3" />
                    <span>AI Analysis in progress</span>
                    <Loader2 className="h-3 w-3 ml-1 animate-spin" />
                  </div>
                )}
              </div>
            ))
          ) : (
            <div className="bg-muted/30 rounded p-2 text-xs text-muted-foreground">
              No documents found for this requirement.
            </div>
          )}
        </div>
      )}
      
      {assessmentDoc && (
        <AssessmentDrawer 
          open={isAssessmentOpen}
          onOpenChange={setIsAssessmentOpen}
          assessment={assessmentDoc.assessment}
          citations={assessmentDoc.citations}
          fileName={assessmentDoc.file_name}
        />
      )}
    </div>
  );
}
